/**
 * useScheduleDraftAnalysis — ScheduleDraft deterministic analysis query.
 *
 * The backend owns rotation and constraint calculations. The frontend only
 * fetches and displays the analysis result returned by the V3 analyze endpoint.
 */

import { useRef } from "react";
import { useQuery } from "react-query";
import { scheduleDraftQueryKeys } from "./queryKeys";
import { scheduleDraftDataService } from "../services/scheduleDraftDataService";

const ANALYSIS_SIGNATURE_FLIGHT_FIELDS = [
    "id",
    "track_id",
    "aircraft_reg",
    "departure_airport",
    "arrival_airport",
    "std",
    "sta",
    "flight_route_id",
    "block_time_minutes",
    "min_block_time_minutes",
    "ground_time_minutes",
    "min_ground_time_minutes",
    "rotation_no",
    "rotation_leg_index",
    "rotation_index",
    "display_order",
    "is_fixed",
    "change_status",
];

function normalizeSignatureValue(value) {
    return value === undefined ? null : value;
}

function buildRouteSnapshotSignature(routeSnapshot) {
    if (!routeSnapshot || typeof routeSnapshot !== "object") {
        return null;
    }

    return {
        id: normalizeSignatureValue(routeSnapshot.id),
        route_name: normalizeSignatureValue(routeSnapshot.route_name),
        departure_airport: normalizeSignatureValue(routeSnapshot.departure_airport),
        arrival_airport: normalizeSignatureValue(routeSnapshot.arrival_airport),
        block_time_minutes: normalizeSignatureValue(routeSnapshot.block_time_minutes),
        min_block_time_minutes: normalizeSignatureValue(routeSnapshot.min_block_time_minutes),
    };
}

function buildWorkingFlightSignature(flight = {}) {
    const signatureFlight = ANALYSIS_SIGNATURE_FLIGHT_FIELDS.reduce((result, field) => {
        result[field] = normalizeSignatureValue(flight?.[field]);
        return result;
    }, {});
    signatureFlight.route_snapshot = buildRouteSnapshotSignature(flight?.route_snapshot);
    return signatureFlight;
}

export function buildScheduleDraftAnalysisRequestSignature(payload = {}) {
    return JSON.stringify({
        working_flights: Array.isArray(payload?.working_flights)
            ? payload.working_flights.map(buildWorkingFlightSignature)
            : null,
    });
}

export function useScheduleDraftAnalysis(draftId, trackId, options = {}) {
    const { requestPayload = {}, requestPayloadRef: externalRequestPayloadRef, ...queryOptions } = options;
    const internalRequestPayloadRef = useRef(requestPayload);
    internalRequestPayloadRef.current = requestPayload;
    const requestPayloadRef = externalRequestPayloadRef || internalRequestPayloadRef;

    return useQuery(
        scheduleDraftQueryKeys.analysis(draftId, trackId),
        async () => {
            const effectiveRequestPayload = requestPayloadRef.current || {};
            const requestPayloadSignature = buildScheduleDraftAnalysisRequestSignature(effectiveRequestPayload);
            const result = await scheduleDraftDataService.analyzeDraft(draftId, trackId, effectiveRequestPayload);
            return result && typeof result === "object"
                ? { ...result, _requestPayloadSignature: requestPayloadSignature }
                : result;
        },
        {
            enabled: Boolean(draftId && trackId),
            staleTime: 0,
            refetchOnWindowFocus: false,
            ...queryOptions,
        },
    );
}

export default useScheduleDraftAnalysis;
